import React, { useMemo, useState } from "react";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import ProductIconTile from "../ProductIconTile";
import { TemplateMessagePreviewFrame } from "../TemplateMessagePreview";

const DEFAULT_INITIAL_VISIBLE_COUNT = 6;

function normalizeSearchValue(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

function defaultGetTemplateId(template) {
  return String(template?.template_id || template?.id || "").trim();
}

function defaultGetTemplateTitle(template) {
  return String(template?.title || template?.name || "Modelo sem nome").trim();
}

function defaultGetTemplateTierLabel(template) {
  const tier = String(
    template?.message_meta_tier ||
      template?.category ||
      template?.message_tag ||
      "",
  ).toLowerCase();

  if (tier.includes("market")) return "Marketing";
  if (tier.includes("auth")) return "Autent.";
  if (tier.includes("util")) return "Utilidade";
  return "Utilidade";
}

function parseTemplateMessageJson(template) {
  const source = template?.messageJson || template?.message_json;
  if (!source) return null;

  try {
    return typeof source === "string" ? JSON.parse(source) : source;
  } catch {
    return null;
  }
}

function defaultGetTemplatePreviewText(template) {
  const parsed = parseTemplateMessageJson(template);
  const firstMessage = parsed?.messages?.[0];
  const rawMessage = firstMessage?.message;
  const text = typeof rawMessage === "string"
    ? rawMessage
    : rawMessage?.content || firstMessage?.content || firstMessage?.body || "";

  return String(text || "Prévia indisponível para este modelo.")
    .replace(/\s+/g, " ")
    .trim();
}

function TemplateMessageChoiceRow({
  getTemplateId,
  getTemplatePreviewText,
  getTemplateTierLabel,
  getTemplateTitle,
  onChoose,
  selected = false,
  template,
}) {
  const templateId = getTemplateId(template);
  const title = getTemplateTitle(template);
  const tierLabel = getTemplateTierLabel(template);
  const previewText = getTemplatePreviewText(template);

  return (
    <Box
      component="button"
      onClick={() => onChoose(templateId)}
      sx={{
        alignItems: "center",
        backgroundColor: selected ? "rgb(238, 242, 255)" : "common.white",
        border: selected
          ? "1px solid rgb(99, 102, 241)"
          : "1px solid rgb(226, 232, 240)",
        borderRadius: "8px",
        color: "rgb(31, 42, 68)",
        cursor: "pointer",
        display: "grid",
        font: "inherit",
        gap: 1,
        gridTemplateColumns: "minmax(0, 1fr) 24px",
        minHeight: 50,
        px: 1,
        py: 0.85,
        textAlign: "left",
        width: "100%",
        "&:hover": {
          backgroundColor: selected ? "rgb(238, 242, 255)" : "rgb(248, 250, 252)",
          borderColor: "rgb(99, 102, 241)",
        },
      }}
      type="button"
    >
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ alignItems: "center", display: "flex", gap: 0.75, minWidth: 0 }}>
          <Typography
            noWrap
            sx={{
              color: selected ? "rgb(79, 70, 229)" : "rgb(31, 42, 68)",
              fontSize: 13,
              fontWeight: 500,
              lineHeight: "19.5px",
            }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              backgroundColor: "rgb(199, 210, 254)",
              borderRadius: "6px",
              color: "rgb(99, 102, 241)",
              flexShrink: 0,
              fontSize: 10,
              fontWeight: 600,
              lineHeight: "15px",
              p: "4px",
            }}
          >
            {tierLabel}
          </Box>
        </Box>
        <Typography
          noWrap
          sx={{
            color: "rgb(71, 85, 105)",
            fontSize: 12,
            fontWeight: 400,
            lineHeight: "18px",
            mt: 0.25,
          }}
        >
          {previewText}
        </Typography>
      </Box>
      <Box
        sx={{
          alignItems: "center",
          border: selected ? "2px solid rgb(99, 102, 241)" : "2px solid rgb(100, 116, 139)",
          borderRadius: "50%",
          display: "flex",
          height: 20,
          justifyContent: "center",
          justifySelf: "end",
          mt: 0.25,
          width: 20,
        }}
      >
        {selected ? (
          <Box
            sx={{
              backgroundColor: "rgb(99, 102, 241)",
              borderRadius: "50%",
              height: 10,
              width: 10,
            }}
          />
        ) : null}
      </Box>
    </Box>
  );
}

export default function TemplateMessageSelector({
  emptyMessage = "Nenhum modelo encontrado.",
  getTemplateId = defaultGetTemplateId,
  getTemplatePreviewText = defaultGetTemplatePreviewText,
  getTemplateTierLabel = defaultGetTemplateTierLabel,
  getTemplateTitle = defaultGetTemplateTitle,
  initialVisibleCount = DEFAULT_INITIAL_VISIBLE_COUNT,
  loading = false,
  onSelectedTemplateIdChange,
  preview,
  previewParameters,
  previewShowParameterState = false,
  searchPlaceholder = "Pesquisar modelo de mensagem",
  selectedTemplateId,
  showHeader = true,
  subtitle = "Selecione um modelo de mensagem",
  templates = [],
  title = "Modelo de mensagem",
}) {
  const [templateSearchTerm, setTemplateSearchTerm] = useState("");
  const [showAllTemplates, setShowAllTemplates] = useState(false);
  const safeTemplates = useMemo(
    () => (Array.isArray(templates) ? templates : []),
    [templates],
  );

  const visibleTemplates = useMemo(() => {
    const normalizedSearch = normalizeSearchValue(templateSearchTerm);
    if (!normalizedSearch) return safeTemplates;

    return safeTemplates.filter((template) => {
      const searchable = [
        getTemplateTitle(template),
        getTemplateTierLabel(template),
        getTemplatePreviewText(template),
      ].join(" ");

      return normalizeSearchValue(searchable).includes(normalizedSearch);
    });
  }, [
    getTemplatePreviewText,
    getTemplateTierLabel,
    getTemplateTitle,
    safeTemplates,
    templateSearchTerm,
  ]);

  const canLoadAllTemplates =
    visibleTemplates.length > initialVisibleCount && !showAllTemplates;
  const displayedTemplates = showAllTemplates
    ? visibleTemplates
    : visibleTemplates.slice(0, initialVisibleCount);

  return (
    <Stack spacing={1.5}>
      {showHeader ? (
        <Box sx={{ alignItems: "center", display: "flex", gap: 1.5 }}>
          <ProductIconTile icon="quickreply" tone="pink" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ color: "rgb(15, 23, 42)", fontSize: 18, fontWeight: 600, lineHeight: "24px" }}>
              {title}
            </Typography>
            <Typography sx={{ color: "rgb(71, 85, 105)", fontSize: 13, lineHeight: "18px" }}>
              {subtitle}
            </Typography>
          </Box>
        </Box>
      ) : null}

      {loading ? (
        <Box sx={{ alignItems: "center", display: "flex", justifyContent: "center", minHeight: 180 }}>
          <CircularProgress size={24} />
        </Box>
      ) : null}

      {!loading ? (
        <Box
          sx={{
            display: "grid",
            gap: 1,
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 0.95fr) minmax(0, 1fr)" },
            minHeight: { md: 347 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: { md: 347 },
              minHeight: { xs: 0, md: 347 },
            }}
          >
            <Box
              sx={{
                alignItems: "center",
                border: "1px solid rgb(203, 213, 225)",
                borderRadius: "6px",
                display: "flex",
                flexShrink: 0,
                height: 40,
                mb: 1,
                px: 1.25,
              }}
            >
              <SearchRoundedIcon sx={{ color: "rgb(71, 85, 105)", fontSize: 20, mr: 1 }} />
              <Box
                component="input"
                onChange={(event) => {
                  setTemplateSearchTerm(event.target.value);
                  setShowAllTemplates(false);
                }}
                placeholder={searchPlaceholder}
                sx={{
                  backgroundColor: "transparent",
                  border: 0,
                  color: "rgb(30, 41, 59)",
                  flex: 1,
                  font: "inherit",
                  fontSize: 13,
                  minWidth: 0,
                  outline: 0,
                  "&::placeholder": {
                    color: "rgb(100, 116, 139)",
                    opacity: 0.8,
                  },
                }}
                value={templateSearchTerm}
              />
            </Box>
            <Stack
              spacing={1}
              sx={{
                flex: { md: 1 },
                maxHeight: { xs: 260, md: "none" },
                minHeight: 0,
                overflowY: "auto",
                pr: 0.75,
                scrollbarColor: "rgb(100, 116, 139) transparent",
                scrollbarWidth: "thin",
              }}
            >
              {displayedTemplates.map((template) => {
                const templateId = getTemplateId(template);

                return (
                  <TemplateMessageChoiceRow
                    getTemplateId={getTemplateId}
                    getTemplatePreviewText={getTemplatePreviewText}
                    getTemplateTierLabel={getTemplateTierLabel}
                    getTemplateTitle={getTemplateTitle}
                    key={templateId}
                    onChoose={onSelectedTemplateIdChange}
                    selected={templateId === selectedTemplateId}
                    template={template}
                  />
                );
              })}
              {!visibleTemplates.length ? (
                <Box sx={{ color: "rgb(100, 116, 139)", fontSize: 13, px: 1, py: 3, textAlign: "center" }}>
                  {emptyMessage}
                </Box>
              ) : null}
            </Stack>
            {canLoadAllTemplates ? (
              <Box sx={{ alignItems: "center", display: "flex", minHeight: 40, mt: 1 }}>
                <Button
                  onClick={() => setShowAllTemplates(true)}
                  size="small"
                  sx={{
                    backgroundColor: "common.white",
                    border: 0,
                    borderRadius: 999,
                    boxShadow: "none",
                    color: "rgb(79, 70, 229)",
                    fontSize: 13,
                    fontWeight: 500,
                    height: 36,
                    justifyContent: "center",
                    lineHeight: "20px",
                    minWidth: 152,
                    px: "12px",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "common.white",
                      border: 0,
                      boxShadow: "none",
                    },
                  }}
                  variant="text"
                >
                  Ver todos os modelos
                </Button>
              </Box>
            ) : null}
          </Box>

          <TemplateMessagePreviewFrame
            parameters={previewParameters}
            preview={preview}
            showParameterState={previewShowParameterState}
            variant="selector"
          />
        </Box>
      ) : null}
    </Stack>
  );
}
